import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X } from "lucide-react";
import { useCart } from "@/lib/CartContext";
import { Badge } from "@/components/ui/badge";
import CartSheet from "@/components/CartSheet";
import {
  Sheet,
  SheetTrigger,
} from "@/components/ui/sheet";

export default function Navbar() {
  const { totalItems, cartOpen, setCartOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const links = [
    { to: "/", label: "Home" },
    { to: "/menu", label: "Menu" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="main-navbar"
      className="sticky top-0 z-50 bg-[#FFFBEB]/80 backdrop-blur-xl border-b border-stone-200/50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 group"
          >
            <span className="font-heading text-xl sm:text-2xl font-bold text-[#78350F] group-hover:text-[#EA580C] transition-colors">
              Meals-O-Meals
            </span>
            <span className="hidden sm:inline text-xs font-body text-stone-400 tracking-widest uppercase">
              M.O.M
            </span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-8">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`font-body text-sm font-medium transition-colors relative pb-1 ${
                  isActive(link.to)
                    ? "text-[#EA580C]"
                    : "text-stone-600 hover:text-[#EA580C]"
                }`}
              >
                {link.label}
                {isActive(link.to) && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#EA580C] rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Cart + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="cart-button"
                  className="relative p-2 rounded-full hover:bg-stone-100 transition-colors"
                >
                  <ShoppingCart className="h-5 w-5 text-[#78350F]" />
                  {totalItems > 0 && (
                    <Badge
                      data-testid="cart-badge"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px] bg-[#EA580C] text-white border-2 border-[#FFFBEB] rounded-full"
                    >
                      {totalItems}
                    </Badge>
                  )}
                </button>
              </SheetTrigger>
              <CartSheet
                onCheckout={() => {
                  setCartOpen(false);
                  navigate("/checkout");
                }}
              />
            </Sheet>

            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 rounded-lg hover:bg-stone-100 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? (
                <X className="h-5 w-5 text-stone-700" />
              ) : (
                <Menu className="h-5 w-5 text-stone-700" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden pb-4 animate-fade-in"
          >
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`block py-2 px-3 rounded-lg font-body text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? "text-[#EA580C] bg-orange-50"
                    : "text-stone-600 hover:bg-stone-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
